import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import generalSettings from "@apis/generalSettings";
import CardHeader from "@commons/businesscardHeader";
import Wrapper from "@components/wrapper";
import Column from "@components/column";
import Button from "@components/button";
import Fields from "@components/fields";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import useConfig from "@hooks/useConfig";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus,
  Trash2,
  Save,
  Image as ImageIcon,
  ChevronUp,
  Layout,
  Store,
  Truck,
  User,
} from "lucide-react";
import {
  ActionButtons,
  EmptyState,
  FormGrid,
  ImagePreview,
  SlideCard,
  CreateContainer,
  ImageSection,
  ContentSection,
  NoImagePlaceholder,
  PageHeader,
  Badge,
  TabsContainer,
  Tab,
} from "./style";

const Onboarding = () => {
  const [isAdding, setIsAdding] = useState(false);
  const [activeRole, setActiveRole] = useState("customer");
  const { data: onboardingData, loading } = useSelector(
    (state: RootState) => state.onboarding,
  );
  const { bucketUrl } = useConfig();

  const fetchSlides = async (role: string) => {
    try {
      await generalSettings.getByGroup({
        id: role,
        state: "onboarding",
      });
    } catch (err) {
      console.error("Failed to fetch onboarding slides", err);
    }
  };

  useEffect(() => {
    fetchSlides(activeRole);
  }, [activeRole]);

  const handleCreate = async (values: any, { resetForm }: any) => {
    if (!values.title || !values.description || !values.file) {
      return toast.error("Please fill all fields and upload an image.");
    }

    try {
      const formData = new FormData();
      formData.append("group", activeRole);
      formData.append("key", `${activeRole}-onboarding-${Date.now()}`);
      formData.append("title", values.title);
      formData.append("value", values.description);
      if (values.file?.rawFile) {
        formData.append("file", values.file.rawFile);
      }

      await generalSettings.create({
        data: formData,
      });
      toast.success(
        `${activeRole.charAt(0).toUpperCase() + activeRole.slice(1)} onboarding slide added successfully!`,
      );
      resetForm();
      setIsAdding(false);
      fetchSlides(activeRole);
    } catch (err: any) {
      toast.error(err.message || "Failed to create slide");
    }
  };

  const handleUpdate = async (id: number, values: any) => {
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("value", values.description);
      formData.append("key", values.key);
      formData.append("group", slideGroup(id) || activeRole);

      if (values.newFile?.rawFile) {
        formData.append("file", values.newFile.rawFile);
      }

      await generalSettings.update({
        id,
        data: formData,
      });
      toast.success("Slide updated successfully!");
      fetchSlides(activeRole);
    } catch (err: any) {
      toast.error(err.message || "Failed to update slide");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this slide?")) return;
    try {
      await generalSettings.remove({
        id,
      });
      toast.success("Slide removed successfully!");
      fetchSlides(activeRole);
    } catch (err: any) {
      toast.error(err.message || "Failed to remove slide");
    }
  };

  const onboardingSlides = Array.isArray(onboardingData) ? onboardingData : [];

  const slideGroup = (id: number) => {
    return onboardingSlides.find((s: any) => s.id === id)?.group;
  };

  const ROLES = [
    { id: "customer", label: "Customer", icon: <User size={16} /> },
    { id: "store", label: "Store", icon: <Store size={16} /> },
    { id: "delivery", label: "Delivery", icon: <Truck size={16} /> },
  ];

  return (
    <Column >
      <PageHeader>
        <CardHeader title="Onboarding Management" />
        <Button
          onClick={() => setIsAdding(!isAdding)}
          title={isAdding ? "Close Form" : "Add New Slide"}
          variant={isAdding ? "secondary" : "primary"}
        />
      </PageHeader>

      <TabsContainer>
        {ROLES.map((role) => (
          <Tab
            key={role.id}
            active={activeRole === role.id}
            onClick={() => {
              setActiveRole(role.id);
              setIsAdding(false);
            }}
          >
            {role.icon}
            {role.label}
          </Tab>
        ))}
      </TabsContainer>

      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ height: 0, opacity: 0, marginBottom: 0 }}
            animate={{ height: "auto", opacity: 1, marginBottom: 32 }}
            exit={{ height: 0, opacity: 0, marginBottom: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <CreateContainer>
              <h3
                style={{
                  marginBottom: "24px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Plus size={20} color="#ef4444" /> New{" "}
                {activeRole.charAt(0).toUpperCase() + activeRole.slice(1)}{" "}
                Onboarding Slide
              </h3>
              <Formik
                initialValues={{ title: "", description: "", file: null }}
                onSubmit={handleCreate}
              >
                {({ setFieldValue }) => (
                  <Form>
                    <FormGrid>
                      <ImageSection>
                        <Fields.Upload
                          label="Slide Image"
                          name="file"
                          height={160}
                          useRawFile={true}
                          onChange={(file: any) => setFieldValue("file", file)}
                        />
                      </ImageSection>
                      <ContentSection>
                        <Fields.Input
                          label="Title"
                          name="title"
                          placeholder={
                            activeRole === "customer"
                              ? "e.g., Shop from your favorite stores"
                              : activeRole === "store"
                                ? "e.g., Manage your business effectively"
                                : "e.g., Earn more with every delivery"
                          }
                          styleInput="width:100%"
                        />
                        <Fields.TextArea
                          label="Description"
                          name="description"
                          placeholder="Describe this onboarding step to your users..."
                          styleInput="width:100%; min-height: 100px"
                        />
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: "12px",
                          }}
                        >
                          <Button
                            type="submit"
                            title="Publish Slide"
                            prefix={<Save size={18} />}
                          />
                        </div>
                      </ContentSection>
                    </FormGrid>
                  </Form>
                )}
              </Formik>
            </CreateContainer>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "16px",
        }}
      >
        <Layout size={18} color="#64748b" />
        <h4 style={{ margin: 0, color: "#475569" }}>
          {activeRole.charAt(0).toUpperCase() + activeRole.slice(1)} Slides (
          {onboardingSlides.length})
        </h4>
      </div>

      <Wrapper>
        {loading ? (
          <div
            style={{ padding: "40px", textAlign: "center", color: "#64748b" }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
              style={{ display: "inline-block", marginBottom: "12px" }}
            >
              <Layout size={24} />
            </motion.div>
            <p>Loading slides...</p>
          </div>
        ) : onboardingSlides.length === 0 ? (
          <EmptyState>
            <div
              style={{
                background: "#f1f5f9",
                padding: "20px",
                borderRadius: "50%",
              }}
            >
              <ImageIcon size={48} color="#94a3b8" />
            </div>
            <h3>
              No {activeRole.charAt(0).toUpperCase() + activeRole.slice(1)}{" "}
              Slides Yet
            </h3>
            <p>
              Create visual onboarding screens to guide your {activeRole}s
              through the app experience.
            </p>
            <Button
              onClick={() => setIsAdding(true)}
              title="Add Your First Slide"
            />
          </EmptyState>
        ) : (
          <AnimatePresence>
            {[...onboardingSlides]
              .sort(
                (a, b) =>
                  new Date(a.createdAt).getTime() -
                  new Date(b.createdAt).getTime(),
              )
              .map((slide: any, index: number) => (
                <SlideCard
                  key={slide.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Badge>Slide #{index + 1}</Badge>
                  <Formik
                    initialValues={{
                      title: slide.title,
                      description: slide.value,
                      file: slide.file,
                      key: slide.key,
                      newFile: null,
                    }}
                    onSubmit={(values) => handleUpdate(slide.id, values)}
                  >
                    {({ setFieldValue, values }) => (
                      <Form style={{ display: "contents" }}>
                        <ImageSection>
                          {values.newFile ? (
                            <ImagePreview
                              src={URL.createObjectURL(values.newFile.rawFile)}
                              alt="Preview"
                            />
                          ) : slide.file ? (
                            <ImagePreview
                              src={`${bucketUrl}/${slide.file}`}
                              alt={slide.title}
                            />
                          ) : (
                            <NoImagePlaceholder>
                              <ImageIcon />
                              <span>No image uploaded</span>
                            </NoImagePlaceholder>
                          )}
                          <Fields.Upload
                            name="newFile"
                            useRawFile={true}
                            onChange={(file: any) =>
                              setFieldValue("newFile", file)
                            }
                            height={60}
                          />
                        </ImageSection>

                        <ContentSection>
                          <Fields.Input
                            label="Title"
                            name="title"
                            styleInput="width:100%"
                          />
                          <Fields.TextArea
                            label="Description"
                            name="description"
                            styleInput="width:100%; min-height: 80px"
                          />
                        </ContentSection>

                        <ActionButtons>
                          <Button
                            type="submit"
                            title="Update"
                            variant="primary"
                            style={{ width: "100%" }}
                          />
                          <Button
                            type="button"
                            title="Delete"
                            variant="danger"
                            onClick={() => handleDelete(slide.id)}
                            style={{ width: "100%" }}
                          />
                        </ActionButtons>
                      </Form>
                    )}
                  </Formik>
                </SlideCard>
              ))}
          </AnimatePresence>
        )}
      </Wrapper>
    </Column>
  );
};

export default Onboarding;
